import React, { FC, useState, useEffect , SetStateAction } from 'react';

import { ComponentsType, LucidTheme, mqMin, variant } from '@lucid/theme';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import styled from '@emotion/styled';

import VideoPlayButton from '../video-play-button/video-play-button';
import { VideoProps } from '../featured-video/featured-video';
import { VideoPlaceholder, VideoOverlay } from '../video-player/video-player';
import Box from '../box/box';
import ImageWithPlaceholder from '../image/image-with-placeholder';
import Icon from '../icon/icon';
import Text from '../text/text';
import Link from '../link/link';
import AccessibleDescription from '../accessible-description/accessible-description';

export interface VideoCarouselProps extends ReactPlayerProps {
  /** The videos to be rendered in the Video Carousel */
  videos: VideoProps[];
  /** Used to set the current video index from parent componnt */
  parentVideoIndex?: number;
  /** Sets the current video index for parent component */
  setParentVideoIndex?: (value: SetStateAction<number>) => void;
  /** Sets the video ratio, with the default being `wide`. */
  ratio?: 'square' | 'wide';
  /** Sets the slide animation timing (in ms) */
  animationTiming?: number;
  /** Sets the variant of the Video Carousel */
  variant?: ComponentsType['VideoCarousel'];
}

const VideoCarouselContainer = styled(Box)(
  variant({
    scale: 'videoCarousel',
    variants: {
      defaultVariant: {}
    }
  }),{
    flex: '1 1 100%',
    overflow: 'hidden',
    [mqMin.tabletLandscape]: {
      flex: '0 1 60%'
    }
  }
);

const VideoCarousel: FC<VideoCarouselProps> = ({
  videos,
  parentVideoIndex,
  setParentVideoIndex,
  ratio = 'wide',
  animationTiming = 600,
  variant = 'default',
  ...rest
}) => {
  const [videoPlayStates, setVideoPlayStates] = useState(videos.map(() => ({
    isPlaying: false,
    isOverlayVisible: true
  })));
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('');

  useEffect(() => {
    // allows parent component access to current video index
    if (parentVideoIndex !== undefined && setParentVideoIndex) {
      setParentVideoIndex(currentVideoIndex);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentVideoIndex, currentVideoIndex]);

  const handlePlayStateChange = ((playState: boolean, index: number) => {
    setVideoPlayStates([...videoPlayStates].map((obj, idx) => (
      index === idx
        ? {
            isPlaying: playState,
            isOverlayVisible: obj.isOverlayVisible
          }
        : obj
    )));
  });

  const handleOverlayStateChange = ((overlayState: boolean, index: number) => {
    setVideoPlayStates([...videoPlayStates].map((obj, idx) => (
      index === idx
        ? {
            isPlaying: obj.isPlaying,
            isOverlayVisible: overlayState
          }
        : obj
    )));
  });

  useEffect(() => {
    setTimeout(() => setSlideDirection(''), animationTiming);
    setVideoPlayStates([...videoPlayStates].map((obj, idx) => (
      idx !== currentVideoIndex ?
          {
            isPlaying: false,
            isOverlayVisible: true
          } : obj
    )));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentVideoIndex]);

  const handleChangeVideo = ((direction: string) => {
    if (currentVideoIndex !== 0 && direction === 'left') {
      setSlideDirection(direction);
      setTimeout(() => setCurrentVideoIndex(currentVideoIndex - 1) , (animationTiming / 2));
    } else if (currentVideoIndex !== videos.length - 1 && direction === 'right') {
      setSlideDirection(direction);
      setTimeout(() => setCurrentVideoIndex(currentVideoIndex + 1), (animationTiming / 2));
    }
  });

  return (
    <VideoCarouselContainer variant={variant}>
      {videos && (
        <Box
          css={(theme: LucidTheme) => ([{
            label: 'videoContainer',
            display: 'block',
            padding: 0,
            minWidth: '100%',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '5px',
            paddingTop: ratio === 'wide' ? '56.25%' : '100%'
          },
          slideDirection === 'right' && {
            animation: `${theme.keyframe.slideFromRight} ${animationTiming / 2}ms ease-out ${animationTiming / 2}ms,
            ${theme.keyframe.slideToLeft} ${animationTiming / 2}ms ease-in`
          },
          slideDirection === 'left' && {
            animation: `${theme.keyframe.slideFromLeft} ${animationTiming / 2}ms ease-out ${animationTiming / 2}ms,
            ${theme.keyframe.slideToRight} ${animationTiming / 2}ms ease-in`
          }
          ])}
        >
          {videoPlayStates[currentVideoIndex].isOverlayVisible && (
            <VideoOverlay>
              <VideoPlayButton
                color="primary"
                onClick={() => {
                  if (videos[currentVideoIndex].videoLink) {
                    handlePlayStateChange(true, currentVideoIndex);
                  }
                }}
                variant="large"
              />
            </VideoOverlay>
          )}
          {videoPlayStates[currentVideoIndex].isOverlayVisible && (
            <VideoPlaceholder>
              {videos[currentVideoIndex].videoLinkPlaceholder && (
                <ImageWithPlaceholder
                  alt={videos[currentVideoIndex].videoLinkPlaceholderAlt || ''}
                  src={videos[currentVideoIndex].videoLinkPlaceholder || 'Featured Video Placeholder'}
                />
              )}
            </VideoPlaceholder>
          )}
          {videoPlayStates[currentVideoIndex].isPlaying && (
            <Box
              aria-describedby={videos[currentVideoIndex].videoDescriptionElementId}
              as="div"
              tabIndex={0}
            >
              <ReactPlayer
                {...rest}
                controls
                css={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0
                }}
                height="100%"
                onReady={() => handleOverlayStateChange(false, currentVideoIndex)}
                playing={videoPlayStates[currentVideoIndex].isPlaying}
                url={videos[currentVideoIndex].videoLink}
                width="100%"
              />
            </Box>
          )}
          {videos[currentVideoIndex].videoDescription && (
            <AccessibleDescription
              id={videos[currentVideoIndex].videoDescriptionElementId || ''}
              SRText={videos[currentVideoIndex].videoDescription || ''}
            />
          )}
        </Box>
      )}
      <Box
        css={(theme: LucidTheme) => ({
          display: videos.length > 1 ? 'flex' : 'none',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: theme.space.s32
        })}
      >
        <Link
          asButton
          aria-label={`View previous video, video ${currentVideoIndex}`}
          css={{ visibility: currentVideoIndex !== 0 ? 'visible' : 'hidden' }}
          onClick={() => handleChangeVideo('left')}
          variant="tabs"
        >
          <Icon
            css={(theme: LucidTheme) => ({
              transform: 'rotateY(180deg)',
              color: theme.colors.primary,
              [mqMin.tabletLandscape]: {
                color: variant === 'featuredVideo' ? theme.colors.coolGrey[0] : theme.colors.primary,
              }
            })}
            type="ArrowRight"
          />
        </Link>
        <Text css={{ margin: 0 }} variant="interactiveS500">
          {currentVideoIndex + 1}
          {' '}
          of
          {' '}
          {videos.length}
        </Text>
        <Link
          asButton
          aria-label={`View next video, video ${currentVideoIndex + 2}`}
          css={{ visibility: currentVideoIndex !== videos.length - 1 ? 'visible' : 'hidden' }}
          onClick={() => handleChangeVideo('right')}
          variant="tabs"
        >
          <Icon
            color="primary"
            type="ArrowRight"
          />
        </Link>
      </Box>
    </VideoCarouselContainer>
  );
};

export { VideoCarousel };
export default VideoCarousel;
